import React from "react";
import { Box } from "@mui/material";
import SabiasQue from "../SabiasQue";

// Template33: Página Día 4 - Fushimi Inari (Kioto)
// Contenido desde page.texts, page.images y page.canvasImages (servidor).
const Template33 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
	updatePage,
}) => {
	const isSmallScreen = window.innerWidth <= 600;

	const getImageSrc = (id) => {
		if (page?.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found && found.src) return found.src;
		}
		return undefined;
	};

	const getCanvasImageUrl = (id) => {
		if (page?.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found && found.imageUrl) return found.imageUrl;
		}
		return undefined;
	};

	const parseSabiasQueItem = (html) => {
		if (!html) return null;
		const parser = new DOMParser();
		const doc = parser.parseFromString(html, "text/html");
		const p = doc.querySelector("p");
		if (!p) return null;
		const text = (p.textContent ?? p.innerText ?? "").trim();
		const emojiMatch = text.match(/^([^\s]+)\s+(.*)/s);
		if (emojiMatch)
			return { icono: emojiMatch[1], texto: emojiMatch[2].trim() };
		return null;
	};

	const headerText = page?.texts?.[0]?.content;
	const heroTitleShort = page?.texts?.[1]?.content;
	const heroTitleLong = page?.texts?.[2]?.content;
	const introText = page?.texts?.[3]?.content;
	const fushimiTitle = page?.texts?.[4]?.content;
	const fushimiText = page?.texts?.[5]?.content;
	const monteTitle = page?.texts?.[6]?.content;
	const monteText = page?.texts?.[7]?.content;
	const zorroText = page?.texts?.[8]?.content;
	const consejoText = page?.texts?.[9]?.content;
	const transporteTitle = page?.texts?.[10]?.content;
	const transporteText = page?.texts?.[11]?.content;
	const reservaText = page?.texts?.[12]?.content;
	const sabiasQueTorii = parseSabiasQueItem(page?.texts?.[13]?.content);
	const sabiasQueZorro = parseSabiasQueItem(page?.texts?.[14]?.content);

	const fushimiInariImage = getImageSrc("fushimi-inari-header");
	const inariImage = getImageSrc("inari-1");
	const inariTemploImage = getImageSrc("inari-templo");
	const zorroImage = getImageSrc("zorro");
	const toriImage = getImageSrc("tori");
	const logoImage = getCanvasImageUrl("logo");
	const inariSticker = getCanvasImageUrl("inari-sticker");

	const h3 = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
		fontSize: "14px",
		fontWeight: 700,
		textTransform: "uppercase",
		margin: 0,
	};

	const contentColor = textColor || "#444";

	const p = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
		fontSize: "13px",
		color: contentColor,
		margin: 0,
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "auto",
				width: "100%",
				backgroundColor: backgroundColor || "#f7f7f7",
				color: contentColor,
				position: "relative",
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				"& *:not([data-template-header])": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
					color: "inherit",
				},
				"& p": {
					marginTop: "0px !important",
					marginBottom: "0px !important",
				},
			}}
		>
			<Box
				data-block-id="header"
				data-template-header
				sx={{
					backgroundColor: headerBackgroundColor || "#5ba374",
					color: headerTextColor || "#fff",
					textAlign: "center",
					padding: "15px 0",
					fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
					fontSize: "14px",
					fontWeight: 800,
					flexShrink: 0,
					position: "sticky",
					top: 0,
					zIndex: 1053,
				}}
			>
				{headerText}
				{logoImage && (
					<Box
						component="img"
						src={logoImage}
						alt="logo"
						sx={{
							width: isSmallScreen ? 66 : 80,
							position: "absolute",
							right: 2,
							top: 18,
							zIndex: 999999,
						}}
					/>
				)}
			</Box>

			<Box data-block-id="image-0" sx={{ position: "relative" }}>
				{fushimiInariImage && (
					<Box
						component="img"
						src={fushimiInariImage}
						alt="Fushimi Inari"
						sx={{
							width: "100%",
							height: 194,
							objectFit: "cover",
						}}
					/>
				)}
				{(heroTitleShort || heroTitleLong) && (
					<Box
						component="h1"
						sx={{
							fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
							fontSize: "32px",
							position: "absolute",
							bottom: 0,
							left: "20px",
							color: "#fff",
							textShadow: "2px 2px 4px rgba(0,0,0,0.6)",
							margin: "10px",
						}}
					>
						{isSmallScreen ? heroTitleShort : heroTitleLong}
					</Box>
				)}
				{inariSticker && (
					<Box
						component="img"
						src={inariSticker}
						alt="InariSticker"
						sx={{
							width: 90,
							position: "absolute",
							bottom: 55,
							left: 20,
						}}
					/>
				)}
			</Box>

			{introText && (
				<Box data-block-id="text-1" sx={{ padding: "10px 12px" }} marginTop="-5px">
					<Box
						component="p"
						sx={{
							fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
							fontSize: "13px",
							color: "#333",
							margin: "0 !important",
							marginBottom: "0px !important",
						}}
					>
						{introText}
					</Box>
				</Box>
			)}

			<Box
				data-block-id="text-2"
				sx={{
					padding: "10px 12px 0px",
					display: "flex",
					flexDirection: "column",
					paddingTop: 0,
					gap: "14px",
				}}
			>
				<Box sx={{ display: "flex", gap: "16px", position: "relative" }}>
					{inariImage && (
						<Box
							component="img"
							src={inariImage}
							alt="Inari"
							sx={{
								width: "40%",
								borderRadius: "12px",
								objectFit: "cover",
								height: 110,
							}}
						/>
					)}
					<Box sx={{ width: "60%" }}>
						{fushimiTitle && (
							<Box component="h3" sx={h3}>
								{fushimiTitle}
							</Box>
						)}
						{fushimiText && (
							<Box
								component="p"
								sx={{
									...p,
									margin: "0 !important",
									marginBottom: "0px !important",
								}}
							>
								{fushimiText}
							</Box>
						)}
					</Box>
					{sabiasQueTorii && (
						<Box sx={{ position: "absolute", top: 0, left: "33%" }}>
							<SabiasQue
								icono={sabiasQueTorii.icono}
								texto={sabiasQueTorii.texto}
							/>
						</Box>
					)}
				</Box>

				<Box sx={{ display: "flex", gap: "16px", position: "relative" }}>
					{inariTemploImage && (
						<Box
							component="img"
							src={inariTemploImage}
							alt="InariTemplo"
							sx={{
								width: "40%",
								borderRadius: "12px",
								objectFit: "cover",
								height: 110,
							}}
						/>
					)}
					<Box sx={{ width: "60%" }}>
						{monteTitle && (
							<Box component="h3" sx={h3}>
								{monteTitle}
							</Box>
						)}
						{monteText && (
							<Box
								component="p"
								sx={{
									...p,
									margin: "0 !important",
									marginBottom: "0px !important",
								}}
							>
								{monteText}
							</Box>
						)}
					</Box>
					{sabiasQueZorro && (
						<Box
							sx={{
								position: "absolute",
								top: isSmallScreen ? "-50px" : "-30px",
								right: isSmallScreen ? "59%" : "26%",
							}}
						>
							<SabiasQue
								icono={sabiasQueZorro.icono}
								texto={sabiasQueZorro.texto}
							/>
						</Box>
					)}
					{zorroImage && (
						<Box
							component="img"
							src={zorroImage}
							alt="Zorro"
							sx={{
								width: 110,
								position: "absolute",
								bottom: isSmallScreen ? "-160px" : "-30px",
								left: "-30px",
							}}
						/>
					)}
					{zorroText && (
						<Box
							sx={{
								position: "absolute",
								bottom: isSmallScreen ? "-80px" : "50px",
								left: 0,
								backgroundColor: "#fff8db",
								border: "2px solid #f5c400",
								borderRadius: "16px 16px 16px 0px",
								padding: "10px 12px",
								fontSize: "11px",
								fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
								color: contentColor,
								boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
								maxWidth: 120,
							}}
						>
							{zorroText}
						</Box>
					)}
				</Box>

				{consejoText && (
					<Box
						sx={{
							display: "flex",
							justifyContent: isSmallScreen ? "flex-end" : "flex-start",
						}}
					>
						<Box
							sx={{
								backgroundColor: "#d9e6de",
								padding: "12px",
								borderRadius: "12px",
								display: "flex",
								gap: "16px",
								alignItems: "center",
								position: "relative",
								width: isSmallScreen ? "60%" : "100%",
							}}
						>
							<Box sx={{ width: "100%" }}>
								<Box
									component="p"
									sx={{
										...p,
										margin: "0 !important",
										marginBottom: "0px !important",
									}}
								>
									{consejoText}
								</Box>
							</Box>
						</Box>
					</Box>
				)}

				<Box sx={{ position: "relative" }}>
					<Box sx={{ position: "relative" }}>
						{(transporteTitle || transporteText) && (
							<Box
								sx={{
									backgroundColor: "#d9e6de",
									border: "3px solid #5ba374",
									borderRadius: "12px",
									padding: "14px 16px",
									fontSize: "12px",
									fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
									color: "#333",
									width: "80%",
								}}
							>
								{transporteTitle && (
									<Box
										component="h4"
										sx={{
											margin: 0,
											marginBottom: "8px",
											fontSize: "13px",
											fontWeight: 700,
											color: "#5ba374!important",
											textTransform: "uppercase",
										}}
									>
										{transporteTitle}
									</Box>
								)}
								{transporteText && (
									<Box
										component="p"
										sx={{
											...p,
											margin: "0 !important",
											marginBottom: "0px !important",
										}}
										dangerouslySetInnerHTML={{ __html: transporteText }}
									/>
								)}
							</Box>
						)}
					</Box>
					{toriImage && (
						<Box
							component="img"
							src={toriImage}
							alt="Tori"
							sx={{
								width: 110,
								position: "absolute",
								bottom: isSmallScreen ? "-100px" : 0,
								right: "-30px",
							}}
						/>
					)}
					{reservaText && (
						<Box
							sx={{
								position: "absolute",
								bottom: isSmallScreen ? "-40px" : "80px",
								right: isSmallScreen ? "30px" : "10px",
								backgroundColor: "#fff8db",
								border: "2px solid #f5c400",
								borderRadius: "16px 16px 0px 16px",
								padding: "10px 12px",
								fontSize: "11px",
								fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
								color: contentColor,
								boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
								maxWidth: isSmallScreen ? 200 : 180,
							}}
							dangerouslySetInnerHTML={{ __html: reservaText }}
						/>
					)}
				</Box>
			</Box>
		</Box>
	);
};

Template33.thumbnail = "/images/template-1.png";

export default Template33;
